<template>
  <overlay-template
    :title="'Hourly rate'"
    :primary-number="rate.hourRate"
    :primary-number-unit="'/person'"
    :primary-number-title="'Minimum'"
    :secondary-number="rate.hourBreakEven"
    :good-for="[
      'Making you anxious',
      'Sometimes for bug fixing and testing',
      'Reporting when required legally'
    ]"
    :not-good-for="[
      'Estimating the length of a project',
      'Estimating the complexity of a project',
      'Resourcing',
      'Scheduling a project',
      'Tracking revenue',
      'Forecasting',
      'Goal setting'
    ]"
  >
    <div slot="intro">
      <p class="dark-red-300-text medium">Avoid estimating or scheduling using an hourly rate whenever possible. Here&rsquo;s why:</p>
      <p>Your hourly rate is the amount you charge for one hour of work.</p>
      <p>It&rsquo;s difficult to estimate in hours accurately as time ticks by imperceptibly (e.g. it takes five minutes to sit down, open your laptop, and click on your daily tasks). Not only this, the amount of hours you work on a project has little connection to the monthly financial cycles of your company (think salary, rent, expenses). Remember, you&rsquo;re not selling time, but if you have to sell it, at least sell it smartly and avoid billing in hours.</p>
      <p>Caveat: sometimes having an hourly rate can be handy for quick internal calculations or even unavoidable project tracking (grants, government procurement requirements, maintenance projects, and bug fixing). If you can&rsquo;t fudge it, calculate it.</p>
      <p>Because hours are a task-level estimating unit, we don&rsquo;t need to account for capacity like we do for month, sprint, and week rates.</p>
    </div>
    <p slot="formula">{{ rate.goal.incoming | currency }} total annual revenue &divide; {{ rate.goal.billableEmployees }} billable employees &divide; {{ rate.goal.workingWeeks }} average working weeks &divide; {{ rate.goal.billableHours }} weekly billable hours = <strong>{{ rate.hourRate | currency }}/hour/person</strong></p>
    <p slot="break-even-formula">{{ rate.goal.outgoing | currency }} total annual expenses &divide; {{ rate.goal.billableEmployees }} billable employees &divide; {{ rate.goal.workingWeeks }} average working weeks &divide; {{ rate.goal.billableHours }} weekly billable hours = <strong>{{ rate.hourBreakEven | currency }}/hour/person</strong></p>

    <div
      v-if="rate.hourRate"
      slot="charts"
      class="grid-column-span-3 grid-column-span-2_lg">
      <div class="reverse p2 c1 group">
        <h2>Ballpark task estimator</h2>
        <p class="measure left">Calculate a high-level estimate for your task, meeting, or small project based on people needed and duration.</p>
        <p class="mb2 align-right"><span class="font-size-8 extra-condensed medium price">{{ hours * people * rate.hourRate | currency }}</span> <span
          v-if="rate.hourBreakEven"
          class="font-size-2 bold dark-red-300-text">{{ hours * people * rate.hourBreakEven | currency }}</span>&nbsp;<span class="font-size-1 dark-red-300-text m0 semibold">break even</span></p>
        <lw-range-input
          :label="hours + ' hour task'"
          :min="1"
          :max="100"
          :step="1"
          :class="'clear mb2'"
          v-model="hours"
        />
        <lw-range-input
          :label="peopleLabel"
          :min="1"
          :max="rate.goal.billableEmployees"
          :step="1"
          v-model="people"
        />
      </div>
    </div>
  </overlay-template>
</template>

<script>
import OverlayTemplate from './OverlayTemplate.vue'
import RangeInput from '../../livewires/RangeInput.vue'

export default {
  components: {
    overlayTemplate: OverlayTemplate,
    lwRangeInput: RangeInput
  },
  data () {
    return {
      page: this.$route.params.id,
      people: 1,
      hours: 1
    }
  },
  computed: {
    rate () {
      return this.$store.getters.rateCard(this.page)
    },
    peopleLabel () {
      const label = this.people > 1 ? 'Multiplied by ' + this.people + ' people' : 'For ' + this.people + ' person'
      return label
    }
  }
}
</script>
